import { supabase } from '@/integrations/supabase/client'
import type { Database } from '@/integrations/supabase/types'
import type { NoteMapData } from '@/components/consultant/NoteMap'

type Note = Database['public']['Tables']['notes']['Row']
type NoteInsert = Database['public']['Tables']['notes']['Insert']

interface NoteContent {
  html?: string;
  text?: string;
  version?: string;
  type?: string;
  lastModified?: string;
  mapData?: NoteMapData;
}

export const notesService = {
  // Get user's notes
  async getNotes(userId: string): Promise<Note[]> {
    const { data, error } = await supabase
      .from('notes')
      .select('*')
      .eq('user_id', userId)
      .order('updated_at', { ascending: false })

    if (error) {
      console.error('Error fetching notes:', error)
      throw error
    }

    return data || []
  },

  // Get a single note by ID
  async getNote(noteId: string): Promise<Note | null> {
    const { data, error } = await supabase
      .from('notes')
      .select('*')
      .eq('id', noteId)
      .single()

    if (error) {
      console.error('Error fetching note:', error)
      throw error
    }

    return data
  },

  // Create a new note
  async createNote(note: NoteInsert): Promise<Note> {
    const { data, error } = await supabase
      .from('notes')
      .insert(note)
      .select()
      .single()

    if (error) {
      console.error('Error creating note:', error)
      throw error
    }

    return data
  },

  // Update an existing note
  async updateNote(noteId: string, updates: Partial<Note>): Promise<Note> {
    const { data, error } = await supabase
      .from('notes')
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq('id', noteId)
      .select()
      .single()

    if (error) {
      console.error('Error updating note:', error)
      throw error
    }

    return data
  },

  // Delete a note
  async deleteNote(noteId: string): Promise<void> {
    const { error } = await supabase
      .from('notes')
      .delete()
      .eq('id', noteId)

    if (error) {
      console.error('Error deleting note:', error)
      throw error
    }
  },

  // Search notes by title or content (handles both string and JSON content)
  async searchNotes(userId: string, searchTerm: string): Promise<Note[]> {
    const { data, error } = await supabase
      .from('notes')
      .select('*')
      .eq('user_id', userId)
      .or(`title.ilike.%${searchTerm}%,content->>html.ilike.%${searchTerm}%,content->>text.ilike.%${searchTerm}%`)
      .order('updated_at', { ascending: false })

    if (error) {
      console.error('Error searching notes:', error)
      throw error
    }

    return data || []
  },

  // Get notes by tags
  async getNotesByTags(userId: string, tags: string[]): Promise<Note[]> {
    const { data, error } = await supabase
      .from('notes')
      .select('*')
      .eq('user_id', userId)
      .overlaps('tags', tags)
      .order('updated_at', { ascending: false })

    if (error) {
      console.error('Error fetching notes by tags:', error)
      throw error
    }

    return data || []
  },

  // Get all unique tags for a user
  async getUserTags(userId: string): Promise<string[]> {
    const { data, error } = await supabase
      .from('notes')
      .select('tags')
      .eq('user_id', userId)
      .not('tags', 'is', null)

    if (error) {
      console.error('Error fetching user tags:', error)
      throw error
    }

    const allTags = data?.flatMap(note => note.tags || []) || []
    return [...new Set(allTags)]
  },

  // Get note map data
  async getNoteMapData(noteId: string): Promise<NoteMapData | null> {
    const { data, error } = await supabase
      .from('notes')
      .select('content')
      .eq('id', noteId)
      .single()

    if (error) {
      console.error('Error fetching note map data:', error)
      throw error
    }

    if (!data?.content) return null

    const content = data.content as NoteContent
    return content.mapData || null
  },

  // Update note map data
  async updateNoteMapData(noteId: string, mapData: NoteMapData): Promise<Note> {
    // First get the current content
    const { data: currentNote, error: fetchError } = await supabase
      .from('notes')
      .select('content')
      .eq('id', noteId)
      .single()

    if (fetchError) {
      console.error('Error fetching current note:', fetchError)
      throw fetchError
    }

    const currentContent = currentNote?.content as NoteContent || {}
    const updatedContent: NoteContent = {
      ...currentContent,
      mapData,
      lastModified: new Date().toISOString()
    }

    return await this.updateNote(noteId, { content: updatedContent as any })
  },

  // Create a note with map data
  async createNoteWithMap(note: NoteInsert, mapData?: NoteMapData): Promise<Note> {
    const content: NoteContent = {
      html: '',
      version: '1.0',
      type: 'rich-text',
      lastModified: new Date().toISOString(),
      ...(mapData && { mapData })
    }

    const noteWithContent = {
      ...note,
      content: content as any
    }

    return await this.createNote(noteWithContent)
  },

  // Get notes with map data (for notes that have maps)
  async getNotesWithMaps(userId: string): Promise<Note[]> {
    const { data, error } = await supabase
      .from('notes')
      .select('*')
      .eq('user_id', userId)
      .not('content->mapData', 'is', null)
      .order('updated_at', { ascending: false })

    if (error) {
      console.error('Error fetching notes with maps:', error)
      throw error
    }

    return data || []
  }
}
